import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Camera } from "lucide-react";
import { api } from "../api/client";
import { Button, Field, IconButton } from "../components/UIKit";
import { pickImage } from "../lib/imagePicker";

export default function VehicleNew() {
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [plate, setPlate] = useState("");
  const [mileage, setMileage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onPickPhoto = async () => {
    const img = await pickImage({ aspect: [4, 3] });
    if (img) setPhoto(img);
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError(null);
    if (!brand || !model || !plate) {
      setError("Marque, modèle et immatriculation requis");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/vehicles", {
        brand: brand.trim(),
        model: model.trim(),
        year: year ? parseInt(year) : null,
        plate: plate.trim().toUpperCase(),
        mileage: mileage ? parseInt(mileage) : 0,
        photo,
      });
      toast.success("Véhicule ajouté avec succès");
      navigate(`/vehicles/${data.id}`, { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError(
        status === 422
          ? (typeof detail === "string" ? detail : "Certains champs sont invalides.")
          : (typeof detail === "string" && detail) || "Impossible d'enregistrer le véhicule."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", paddingBottom: 100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 20px 12px" }}>
        <IconButton testID="new-vehicle-back" icon={<X size={22} />} onClick={() => navigate(-1)} ariaLabel="Fermer" />
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Nouveau véhicule</h2>
        <div style={{ width: 40 }} />
      </div>

      <form onSubmit={onSubmit} style={{ padding: "0 20px" }}>
        <button
          type="button"
          onClick={onPickPhoto}
          data-testid="new-vehicle-photo"
          style={{
            width: "100%", height: 200, borderRadius: 20, marginBottom: 20,
            background: "var(--mf-bg-card)", border: photo ? "none" : "2px dashed var(--mf-border)",
            display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6,
            overflow: "hidden", cursor: "pointer", boxShadow: photo ? "var(--mf-shadow)" : "none",
          }}
        >
          {photo ? (
            <img src={photo} alt="Véhicule" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <>
              <span style={{
                width: 48, height: 48, borderRadius: 14, background: "rgba(11,25,44,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4,
              }}><Camera size={26} color="var(--mf-brand)" /></span>
              <span style={{ color: "var(--mf-text)", fontSize: 15, fontWeight: 600 }}>Ajouter une photo</span>
              <span style={{ color: "var(--mf-text-muted)", fontSize: 12 }}>Optionnel</span>
            </>
          )}
        </button>

        <Field testID="new-vehicle-brand" label="Marque" value={brand} onChange={setBrand} placeholder="Renault, Peugeot..." />
        <Field testID="new-vehicle-model" label="Modèle" value={model} onChange={setModel} placeholder="Clio, 308..." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12 }}>
          <Field testID="new-vehicle-year" label="Année" value={year} onChange={setYear} placeholder="2022" maxLength={4} />
          <Field testID="new-vehicle-plate" label="Immatriculation" value={plate} onChange={(v) => setPlate(v.toUpperCase())} placeholder="AB-123-CD" />
        </div>
        <Field testID="new-vehicle-mileage" label="Kilométrage actuel" value={mileage} onChange={setMileage} placeholder="0" />

        {error && <div style={{ color: "var(--mf-urgent-text)", textAlign: "center", marginTop: 8, marginBottom: 8, fontSize: 14 }}>{error}</div>}

        <Button testID="new-vehicle-submit" type="submit" loading={loading} style={{ marginTop: 16 }}>
          Ajouter le véhicule
        </Button>
      </form>
    </div>
  );
}
